import { Box } from '@chakra-ui/react';

import type {
  DataBuilderCompanySelections,
  DataBuilderCompanySet,
} from '../../data-builder.store';
import { formatCompanySetName } from '../../utils/selection-summary';
import {
  formatCountLabel,
  SummaryPopover,
  SummaryTrigger,
} from './SummaryPrimitives';

type CompaniesData =
  | { type: 'mapper' }
  | { type: 'default' }
  | {
      type: 'common_set';
      companySets: DataBuilderCompanySet[];
    }
  | {
      type: 'selected';
      entries: [string, DataBuilderCompanySelections[string]][];
    }
  | {
      type: 'mapper_with_selections';
      entries: [string, DataBuilderCompanySelections[string]][];
    };

export const CompaniesSummary = ({
  companies,
}: {
  companies: CompaniesData;
}) => {
  if (companies.type === 'mapper') {
    return <>Selected from List</>;
  }

  if (companies.type === 'default') {
    return <>Default (Revelio Shared)</>;
  }

  if (companies.type === 'common_set') {
    const companySetNames = companies.companySets.map(formatCompanySetName);
    return <>Common set ({companySetNames.join(', ')})</>;
  }

  const label =
    companies.type === 'mapper_with_selections'
      ? 'Selected Companies'
      : 'Selected';

  return (
    <SummaryPopover
      trigger={
        <SummaryTrigger
          text={`${label} (${formatCountLabel(
            companies.entries.length,
            'company',
            'companies'
          )})`}
        />
      }
    >
      {companies.entries.map(([id, company]) => (
        <Box key={id} fontSize="xs">
          {company.primary_name ?? company.label ?? id}
        </Box>
      ))}
    </SummaryPopover>
  );
};

export const buildCompaniesData = (
  companyMapper: unknown,
  companySets: DataBuilderCompanySet[] | null | undefined,
  companyEntries: [string, DataBuilderCompanySelections[string]][]
): CompaniesData => {
  if (companyMapper !== null && companyMapper !== undefined) {
    if (companyEntries.length > 0) {
      return { type: 'mapper_with_selections', entries: companyEntries };
    }
    return { type: 'mapper' };
  }

  if (
    companySets !== null &&
    companySets !== undefined &&
    companySets.length > 0
  ) {
    return { type: 'common_set', companySets };
  }

  if (companyEntries.length === 0) {
    return { type: 'default' };
  }

  return { type: 'selected', entries: companyEntries };
};

export type { CompaniesData };
